import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Users, ShoppingBag, Calendar, Crown, Wallet, User, LogOut, Menu, X, Sparkles, ShieldCheck, Package, Trophy, Gift, IdCard, Info, UsersRound } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import CartDrawer from "@/components/CartDrawer";
import HeaderInstallButton from "@/components/HeaderInstallButton";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SearchBar from "@/components/SearchBar";
import RechargeCelebrationHost from "@/components/RechargeCelebrationHost";
import { useI18n } from "@/i18n/I18nProvider";

const NAV = [
  { to: "/app", labelKey: "nav.dashboard", icon: LayoutDashboard, end: true },
  { to: "/community", labelKey: "nav.community", icon: Users },
  { to: "/marketplace", labelKey: "nav.marketplace", icon: ShoppingBag },
  { to: "/app/orders", labelKey: "nav.orders", icon: Package },
  { to: "/events", labelKey: "nav.events", icon: Calendar },
  { to: "/leadership", labelKey: "nav.leaders", icon: Crown },
  { to: "/leaders", labelKey: "nav.find_leader", icon: UsersRound },
  { to: "/recognition", labelKey: "nav.stars", icon: Trophy },
  { to: "/app/wallet", labelKey: "nav.wallet", icon: Wallet },
  { to: "/app/rewards", labelKey: "nav.rewards", icon: Gift },
  { to: "/app/profile", labelKey: "nav.profile", icon: User },
  { to: "/founder-card", labelKey: "nav.founder_card", icon: IdCard },
  { to: "/about-shakti-wings", labelKey: "nav.about", icon: Info },
];

const ADMIN_NAV = { to: "/app/admin", labelKey: "nav.admin", icon: ShieldCheck };

// Catalog Executives get a stripped-down console: only their product catalog.
const CE_NAV = [{ to: "/app/catalog", label: "Catalog", labelKey: "ce_catalog", icon: Package }];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const navItems = user?.role === "catalog_executive" ? CE_NAV : user?.role === "admin" ? [...NAV, ADMIN_NAV] : NAV;

  const handleLogout = () => {
    logout();
    nav("/");
  };

  return (
    <div className="min-h-screen flex bg-pearl">
      {/* Sidebar - desktop */}
      <aside className="hidden lg:flex flex-col w-72 bg-white border-r border-amethyst-100 sticky top-0 h-screen">
        <div className="p-6 border-b border-amethyst-100 flex items-center justify-between gap-2">
          <NavLink to="/" className="flex items-center gap-2 min-w-0" data-testid="brand-link">
            <img src="/icons/icon-192.png" alt="Shakti Wings Logo" className="w-9 h-9 rounded-lg object-contain flex-shrink-0" />
            <span className="text-2xl font-serif font-semibold text-amethyst-800 truncate">{t("brand.name")}</span>
          </NavLink>
          <div className="flex items-center gap-1">
            <HeaderInstallButton />
            <LanguageSwitcher />
            <CartDrawer />
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.labelKey}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  isActive
                    ? "bg-amethyst-50 text-amethyst-700"
                    : "text-gray-600 hover:bg-amethyst-50/50 hover:text-amethyst-700"
                }`
              }
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              {item.label || t(item.labelKey)}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-amethyst-100">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-amethyst-50/50">
            <Avatar className="w-10 h-10 ring-2 ring-gold-300">
              <AvatarImage src={user?.profile_photo} />
              <AvatarFallback className="bg-amethyst-100 text-amethyst-700">{user?.name?.[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate text-amethyst-800">{user?.name}</p>
              <p className="text-xs text-amethyst-600 capitalize truncate">{user?.role?.replace("_", " ")}</p>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="p-2 hover:bg-amethyst-100 rounded-lg transition-colors"
              aria-label="Logout"
            >
              <LogOut className="w-4 h-4 text-amethyst-600" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 glass-header">
        <div className="flex items-center justify-between px-4 py-3 gap-2">
          <NavLink to="/" className="flex items-center gap-2 min-w-0">
            <img src="/icons/icon-192.png" alt="Shakti Wings Logo" className="w-8 h-8 rounded-lg object-contain flex-shrink-0" />
            <span className="text-xl font-serif font-semibold text-amethyst-800 truncate">{t("brand.name")}</span>
          </NavLink>
          <div className="flex items-center gap-1">
            <HeaderInstallButton variant="mobile" />
            <LanguageSwitcher />
            <CartDrawer variant="mobile" />
            <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn" aria-label="Open menu" className="p-2">
              <Menu className="w-6 h-6 text-amethyst-700" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-amethyst-900/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="relative w-72 bg-white flex flex-col h-full animate-fade-in">
            <div className="p-6 border-b border-amethyst-100 flex items-center justify-between">
              <span className="text-xl font-serif font-semibold text-amethyst-800">Menu</span>
              <button onClick={() => setOpen(false)} aria-label="Close menu">
                <X className="w-6 h-6 text-amethyst-700" />
              </button>
            </div>
            <nav className="flex-1 p-4 space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${item.labelKey}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                      isActive ? "bg-amethyst-50 text-amethyst-700" : "text-gray-600 hover:bg-amethyst-50/50"
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" strokeWidth={1.5} />
                  {item.label || t(item.labelKey)}
                </NavLink>
              ))}
              <button
                onClick={handleLogout}
                data-testid="mobile-logout-btn"
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-5 h-5" strokeWidth={1.5} />
                {t("nav.logout")}
              </button>
            </nav>
          </aside>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 min-w-0 lg:pt-0 pt-16">
        {/* Universal search bar */}
        <div className="bg-white/70 backdrop-blur border-b border-amethyst-100 px-4 sm:px-6 lg:px-8 py-2.5">
          <div className="max-w-3xl mx-auto">
            <SearchBar />
          </div>
        </div>
        <Outlet />
      </main>

      {/* Global recharge-approval celebration modal — polls in the background,
          auto-opens the moment admin approves a member's SMC recharge. */}
      <RechargeCelebrationHost />
    </div>
  );
}
